import {
  Box,
  FormHelperText,
  FormHelperTextProps,
  MenuItem,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';

import { Icon } from './Icon';

import { tx } from '@/theme/tokens';

export interface SelectProps<T extends string | number>
  extends Omit<MuiSelectProps<T>, 'variant' | 'renderValue'>,
    Pick<TextFieldProps, 'placeholder' | 'helperText'> {
  /**
   * The variant to use.
   */
  variant?: MuiSelectProps['variant'] | 'text';
  /**
   * A list of options that will be shown in the Select dropdown.
   */
  options?: T[];
  /**
   * Used to determine the string value for a given option.
   */
  getOptionLabel?: (option: T) => ReactNode;
  /**
   * Props forwarded to the helper text element.
   */
  FormHelperTextProps?: FormHelperTextProps;
}

export const Select = <T extends string | number = string>({
  variant,
  options,
  getOptionLabel,
  value,
  placeholder,
  helperText,
  error,
  FormHelperTextProps: formHelperTextProps,
  ...rest
}: SelectProps<T>) => {
  const isTextVariant = variant === 'text';
  const muiVariant = isTextVariant ? 'standard' : variant;
  const menuProps = rest.MenuProps;
  const menuRootSlot = menuProps?.slotProps?.root as { sx?: object } | undefined;
  const menuPaperSlot = menuProps?.slotProps?.paper as { sx?: object } | undefined;

  const renderValue = (value: T): ReactNode => {
    if (!value && placeholder) {
      return (
        <Typography variant="inherit" color="text.secondary">
          {placeholder}
        </Typography>
      );
    }
    return getOptionLabel?.(value) || (value as ReactNode);
  };

  return (
    <Box>
      <MuiSelect
        variant={muiVariant}
        value={value}
        renderValue={renderValue}
        displayEmpty={!!placeholder}
        error={error}
        {...rest}
        MenuProps={{
          ...menuProps,
          sx: {
            zIndex: (theme) => theme.zIndex.modal + 400,
            ...menuProps?.sx,
          },
          // Replicate the DenseMenu component styles
          slotProps: {
            ...menuProps?.slotProps,
            root: {
              ...menuProps?.slotProps?.root,
              sx: {
                zIndex: (theme) => theme.zIndex.modal + 400,
                ...menuRootSlot?.sx,
              },
            },
            list: {
              ...menuProps?.slotProps?.list,
              dense: true,
              disablePadding: true,
            },
            paper: {
              ...menuProps?.slotProps?.paper,
              sx: {
                padding: 0,
                borderRadius: 2.5,
                maxHeight: 220,
                zIndex: (theme) => theme.zIndex.modal + 401,
                ...menuPaperSlot?.sx,
              },
            },
          },
        }}
        sx={
          isTextVariant
            ? {
                '&::before, &::after': { display: 'none' },
              }
            : {}
        }>
        {options?.map((value, index) => (
          <MenuItem key={index} dense value={value} sx={{ fontSize: 13, py: 0.5 }}>
            {getOptionLabel?.(value) || (value as ReactNode)}
          </MenuItem>
        ))}
      </MuiSelect>
      {helperText && (
        <FormHelperText error={error} {...formHelperTextProps}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

// A hairline chevron; MUI passes className to place and rotate it
export const SelectIcon = (props?: { className?: string }) => (
  <Icon name="chev" size={16} className={props?.className} style={{ color: tx.faint }} />
);

export const selectIcon = SelectIcon();
